import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import { AgxColumn, AgxLabel, AgxRow } from '@urbanx/agx-ui-components';
import {
  useStaffGetAgencyActivitiesSettings,
  useStaffUpdateAgencyActivitiesSettings,
  useInvalidateUseStaffGetAgencyActivitiesSettings,
} from '@urbanx/agx.activities.client';
import {
  AgxButton,
  AgxLoadingState,
  AgxPanel,
  AgxSpacer,
  AgxToggleSwitch,
  AgxTypography,
  Breakpoints,
  Icon_Arrow,
  useDialog,
  useToast,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { MenuTitles, SettingsMenu } from 'constants/menu';
import { useSelectedAgency } from 'hooks';
import { ContentLayout, PageLayout } from 'layout';
import DisableAgencyActivitiesDialog from './DisableAgencyActivitiesDialog';

export default function AgencyActivitiesSettings() {
  const { agencyId } = useParams();
  const { userAccount } = useContext(AuthContext);
  const { agency: selectedAgency } = useSelectedAgency();
  const { pushToast } = useToast();
  const windowSize = useWindowSize();
  const showBackButton =
    windowSize === Breakpoints.Mobile || windowSize === Breakpoints.Tablet_Portrait;
  const [activeMenu, setActiveMenu] = useState<MenuTitles | undefined>(
    MenuTitles.AGENCY_ACTIVITIES
  );
  const confirmation = useDialog(DisableAgencyActivitiesDialog);
  const invalidate = useInvalidateUseStaffGetAgencyActivitiesSettings();
  const { data, isPending, isError } = useStaffGetAgencyActivitiesSettings(
    agencyId ?? '',
    !!agencyId
  );
  const { staffUpdateAgencyActivitiesSettings, staffUpdateAgencyActivitiesSettingsPending } =
    useStaffUpdateAgencyActivitiesSettings({
      onSuccess: async (_, input) => {
        await invalidate(input.agencyId);
        pushToast({ type: 'success', message: 'Agency activities settings updated' });
      },
      onError: () =>
        pushToast({
          type: 'error',
          message: 'Unable to update agency activities settings. Please try again.',
        }),
    });
  const save = async (isWhitelisted: boolean) => {
    if (!agencyId || staffUpdateAgencyActivitiesSettingsPending) return;
    try {
      await staffUpdateAgencyActivitiesSettings({ agencyId, isWhitelisted });
    } catch {
      // The mutation reports the error and keeps the saved toggle state.
    }
  };

  return (
    <PageLayout
      agentName={userAccount?.firstName ?? ''}
      primaryPageTitle={selectedAgency?.name ?? ''}
      currentPageTitle={MenuTitles.SETTINGS}
    >
      <ContentLayout
        hasSecondaryMenu
        secondaryActiveMenu={MenuTitles.SETTINGS}
        tertiaryMenu={SettingsMenu}
        tertiaryActiveMenu={activeMenu}
      >
        <AgxColumn veryLargeGap>
          <AgxRow
            spaceBetween
            centered
            extraClasses={`borderBottomContainer ${!showBackButton ? 'pageTitleContainerWithTertiaryMenu' : ''}`}
          >
            {!showBackButton && <AgxLabel large>{MenuTitles.SETTINGS}</AgxLabel>}
            {showBackButton && (
              <AgxButton
                id="btn-back"
                leftIcon={<Icon_Arrow direction="left" />}
                variant="naked"
                size="large"
                text="Activities"
                onClick={() => setActiveMenu(undefined)}
              />
            )}
          </AgxRow>
          <AgxPanel
            header={
              <AgxTypography type="body_ui_strong" size="medium">
                Activities
              </AgxTypography>
            }
          >
            {isPending ? (
              <AgxLoadingState />
            ) : isError || !data ? (
              <p role="alert">
                Unable to load agency activities settings. Please refresh and try again.
              </p>
            ) : (
              <AgxSpacer direction="horizontal" gap="spacing-lg" contentAlignment="center" fill>
                <AgxToggleSwitch
                  id="agency-whitelist"
                  checked={data.isWhitelisted === true}
                  disabled={staffUpdateAgencyActivitiesSettingsPending}
                  onChange={(checked) => {
                    if (checked) void save(true);
                    else confirmation.open({ onConfirm: () => void save(false) });
                  }}
                />
                <label htmlFor="agency-whitelist">
                  <AgxTypography type="body_ui" size="medium">
                    Task generation
                  </AgxTypography>
                </label>
              </AgxSpacer>
            )}
          </AgxPanel>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  );
}
